import { categories, stories, legacySlideMap } from './catalogue/runtime.ts';
import type { Category, Story } from './stories.ts';
import {validBookmark} from '../shared/bookmarks.ts';
import type {BookmarkDetails} from '../shared/bookmarks.ts';

export type Progress = { index: number; visited: number[]; updatedAt: number; contentRevision?:number };
export type Journal = {
  version: 1; catalogueRevision: 2; saved: string[]; reactions: Record<string, 'like' | 'dislike'>;
  progress: Record<string, Progress>; completed: Record<string, { date: string; timestamp: number }>;
  activeDays: string[]; textSize: number; theme: 'paper' | 'night';
  continueDismissed?: Record<string, number>;
  bookmarks?: Record<string, BookmarkDetails>;
};
export const STORAGE_KEY = 'khoji-park-journal-v1';
export function emptyJournal(): Journal {
  return { version: 1, catalogueRevision: 2, saved: [], reactions: {}, progress: {}, completed: {}, activeDays: [], textSize: 1, theme: 'paper' };
}
const isObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const knownIds={has:(id:string)=>stories.some(story=>story.id===id)};
export function restoreJournal(raw: string | null): Journal {
  const clean = emptyJournal();
  if (!raw) return clean;
  try {
    const value: unknown = JSON.parse(raw);
    if (!isObject(value) || value.version !== 1) return clean;
    if (Array.isArray(value.saved)) clean.saved = [...new Set(value.saved.filter((id): id is string => typeof id === 'string' && knownIds.has(id)))];
    if(isObject(value.bookmarks))for(const [id,details] of Object.entries(value.bookmarks)){
      if(clean.saved.includes(id)&&validBookmark(details))(clean.bookmarks??={})[id]={folder:details.folder,color:details.color};
    }
    if (isObject(value.reactions)) for (const [id, reaction] of Object.entries(value.reactions)) {
      if (knownIds.has(id) && (reaction === 'like' || reaction === 'dislike')) clean.reactions[id] = reaction;
    }
    if (isObject(value.continueDismissed)) for (const [id, timestamp] of Object.entries(value.continueDismissed)) {
      if (knownIds.has(id) && typeof timestamp === 'number' && Number.isFinite(timestamp) && timestamp > 0)
        (clean.continueDismissed ??= {})[id] = timestamp;
    }
    if (isObject(value.progress)) for (const [id, entry] of Object.entries(value.progress)) {
      const story = stories.find(s => s.id === id);
      if (!story || !isObject(entry) || typeof entry.index !== 'number' || !Array.isArray(entry.visited)) continue;
      // Preserve the original chapter on resume after inserting the two new chapters.
      // Newly added chapters remain unread; completed-story awards stay intact.
      const map = value.catalogueRevision === undefined ? legacySlideMap[id] : undefined;
      if (value.catalogueRevision !== undefined && value.catalogueRevision !== 2) continue;
      const sourceLength = map?.length ?? story.slides.length;
      const sourceIndex = Math.max(0, Math.min(sourceLength - 1, Math.floor(entry.index) || 0));
      const visited = entry.visited.filter((n): n is number => Number.isInteger(n) && n >= 0 && n < sourceLength);
      clean.progress[id] = {
        index: map?.[sourceIndex] ?? sourceIndex,
        visited: [...new Set(visited.map(index=>map?.[index] ?? index))],
        updatedAt: typeof entry.updatedAt === 'number' && Number.isFinite(entry.updatedAt) ? entry.updatedAt : 0,
        ...(Number.isSafeInteger(entry.contentRevision)&&Number(entry.contentRevision)>0?{contentRevision:Number(entry.contentRevision)}:{}),
      };
    }
    // Different editions restart unfinished slide progress; earned titles/history remain.
    for(const [id,progress]of Object.entries(clean.progress)){
      const edition=stories.find(story=>story.id===id)?.contentRevision;
      if(edition!==undefined&&progress.contentRevision!==undefined&&edition>progress.contentRevision)clean.progress[id]={index:0,visited:[],updatedAt:progress.updatedAt,contentRevision:edition};
    }
    if (isObject(value.completed)) for (const [id, entry] of Object.entries(value.completed)) {
      if (knownIds.has(id) && isObject(entry) && typeof entry.date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(entry.date) && typeof entry.timestamp === 'number' && Number.isFinite(entry.timestamp)) {
        clean.completed[id] = { date: entry.date, timestamp: entry.timestamp };
      }
    }
    clean.activeDays = [...new Set(Object.values(clean.completed).map(e => e.date))].sort();
    if (value.textSize === 0 || value.textSize === 1 || value.textSize === 2) clean.textSize = value.textSize;
    if (value.theme === 'night') clean.theme = 'night';
    return clean;
  } catch { return clean; }
}
export function wordCount(story: Story) {
  return [story.title, ...story.slides.flatMap(s => [s.title, s.body, s.ending])].join(' ').trim().split(/\s+/).length;
}
export function readingMinutes(story: Story) { return story.readingMinutes ?? Math.max(1, Math.ceil(wordCount(story) / 180)); }
export function readingPercent(journal: Journal, story: Story) {
  if (journal.completed[story.id]) return 100;
  const visited = new Set(journal.progress[story.id]?.visited.filter(index => index >= 0 && index < story.slides.length) ?? []);
  return Math.round(visited.size / story.slides.length * 100);
}

export function tagGroup(tag: string): 'category' | 'place' | 'more' {
  const normalized = tag.toLowerCase();
  if (categories.some(item => item.toLowerCase() === normalized)) return 'category';
  if (stories.some(item => item.tags[1].toLowerCase() === normalized)) return 'place';
  return 'more';
}
export function toggleSearchTag(selected: string[], tag: string) {
  if (selected.includes(tag)) return selected.filter(item => item !== tag);
  return [...selected.filter(item => tagGroup(item) !== tagGroup(tag)), tag];
}

// A story keeps three editorial tags but can appear in several relevant shelves.
const relatedCollections: Record<string, Category[]> = {
  chelembra: ['Crime'], 'banco-rio': ['Crime'], antwerp: ['Crime', 'Mystery'],
  'baker-street': ['Crime', 'Mystery'], gardner: ['Crime', 'Mystery'],
  hanssen: ['Crime'], 'btk-disk': ['Crime'], watergate: ['Crime'],
  'invoice-scam': ['Crime'], ftx: ['Crime', 'Scam'], planter: ['War', 'History'],
  'bangladesh-bank': ['Crime'], 'fontainebleau-heist': ['Crime', 'Mystery'],
};
export const homeShelfCategories: Category[] = ['Crime', 'Spy', 'Mystery', 'Robbery', 'Scam', 'AI', 'War', 'Serial Killer', 'Discovery', 'Movies'];
export function storyInCategory(story: Story, category: Category) {
  return story.category === category || Boolean(story.categoryNames?.includes(category)) || Boolean(relatedCollections[story.id]?.includes(category));
}
export const categoryStoryCounts=new Proxy({} as Record<Category,number>,{get:(_,key)=>stories.filter(story=>storyInCategory(story,key as Category)).length});

export function readingStats(journal: Journal) {
  const completed = stories.filter(story => Boolean(journal.completed[story.id]));
  function breakdown(value: (story: Story) => string) {
    const counts = new Map<string, number>();
    for (const story of completed) counts.set(value(story), (counts.get(value(story)) ?? 0) + 1);
    return [...counts].map(([name, count]) => ({ name, count })).sort((a,b) => b.count - a.count || a.name.localeCompare(b.name, 'en'));
  }
  const byCategory = breakdown(story => story.category);
  const byPlace = breakdown(story => story.tags[1]);
  const topThree = (items: {name: string; count: number}[]) => items.slice(0,3);
  return {
    storiesRead: completed.length,
    storiesSaved: stories.filter(story => journal.saved.includes(story.id)).length,
    totalMinutes: completed.reduce((total, story) => total + readingMinutes(story), 0),
    byCategory, byPlace, topCategories: topThree(byCategory), topPlaces: topThree(byPlace),
  };
}

export function filterStories(query: string, category: Category | 'All' = 'All') {
  const tokenize = (text: string) => text.normalize('NFKD').replace(/\p{M}/gu, '').toLowerCase().match(/[\p{L}\p{N}]+/gu) ?? [];
  const words = tokenize(query);
  return stories.filter(story => {
    if (category !== 'All' && !storyInCategory(story, category)) return false;
    const candidates = tokenize([story.title, story.incident, story.subtitle, story.place, story.country, story.year, ...story.tags, ...(relatedCollections[story.id] ?? [])].join(' '));
    return words.every(word => candidates.some(candidate => word.length <= 2 ? candidate === word : candidate.startsWith(word)));
  });
}

export function searchByTags(query: string, tags: string[]) {
  const selected = [...new Set(tags)];
  if (selected.length < 1 || selected.length > 3) return [];
  if (new Set(selected.map(tagGroup)).size !== selected.length) return [];
  return filterStories(query).filter(story => selected.every(tag => {
    const category = categories.find(item => item.toLowerCase() === tag.toLowerCase());
    return category ? storyInCategory(story, category) : story.tags.some(item => item.toLowerCase() === tag.toLowerCase());
  }));
}

export function continueReading(journal: Journal) {
  return stories.filter(story => !journal.completed[story.id] && journal.progress[story.id]?.visited.length
    && journal.progress[story.id].updatedAt > (journal.continueDismissed?.[story.id] ?? -1))
    .sort((a, b) => journal.progress[b.id].updatedAt - journal.progress[a.id].updatedAt);
}

export function removeFromContinue(journal:Journal,id:string,timestamp=Date.now()):Journal {
  if (!knownIds.has(id) || !journal.progress[id]) return journal;
  return {...journal,continueDismissed:{...journal.continueDismissed,[id]:Math.max(timestamp,journal.progress[id].updatedAt,journal.continueDismissed?.[id]??0)}};
}

export function homeInterests(journal: Journal, catalogue: readonly Story[] = stories) {
  const categories = new Map<string, number>(), places = new Map<string, number>();
  for (const story of catalogue) {
    if (journal.reactions[story.id] === 'dislike') continue;
    const weight = (journal.reactions[story.id] === 'like' ? 3 : 0) + (journal.completed[story.id] ? 2 : 0);
    if (!weight) continue;
    categories.set(story.category, (categories.get(story.category) ?? 0) + weight);
    const place = story.tags[1] || story.country;
    if (place) places.set(place, (places.get(place) ?? 0) + weight);
  }
  const top = (map:Map<string,number>) => [...map].sort((a,b)=>b[1]-a[1]||a[0].localeCompare(b[0],'en')).slice(0,3);
  return {categories,places,topCategories:top(categories),topPlaces:top(places)};
}
export function rankHomeStories(journal: Journal, items: readonly Story[], mode:'recommended'|'category'='category', catalogue: readonly Story[]=stories, preferredCategories:readonly string[]=[]) {
  const interests=homeInterests(journal,catalogue);
  const score=(story:Story)=>mode==='category'?(interests.places.get(story.tags[1]||story.country)??0):
    interests.topCategories.reduce((sum,[category,weight])=>sum+(storyInCategory(story,category as Category)?weight:0),0)+
    interests.topPlaces.reduce((sum,[place,weight])=>sum+((story.tags[1]||story.country)===place?weight:0),0)+
    (preferredCategories.some(category=>storyInCategory(story,category as Category))?8:0);
  const bucket=(story:Story)=>journal.completed[story.id]?2:mode==='recommended'?Number(Boolean(journal.progress[story.id]?.visited.length)):Number(journal.reactions[story.id]==='dislike');
  return [...items].sort((a,b)=>bucket(a)-bucket(b)||score(b)-score(a)||(b.likes??0)-(a.likes??0)||a.id.localeCompare(b.id,'en'));
}
export function recommendedStories(journal: Journal, limit = 10, preferredCategories:readonly string[]=[]) {
  const candidates=stories.filter(story=>journal.reactions[story.id]!=='dislike');
  return rankHomeStories(journal,candidates,'recommended',stories,preferredCategories).slice(0,Math.max(0,Math.min(10,limit)));
}
export function categoryHomeStories(journal:Journal,category:Category,catalogue:readonly Story[]=stories){
  const all=catalogue.filter(story=>storyInCategory(story,category));
  const unread=all.filter(story=>!journal.completed[story.id]);
  return rankHomeStories(journal,unread.length?unread:all,'category',catalogue);
}
export function localDate(date = new Date()) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}
const dayNumber = (s: string) => Math.floor(Date.parse(s + 'T00:00:00Z') / 86_400_000);
export function currentStreak(days: string[], today = localDate()) {
  const uniqueDays = [...new Set(days)].map(dayNumber).filter(Number.isFinite).sort((a, b) => b - a);
  const todayNumber = dayNumber(today);
  if (!uniqueDays.length || uniqueDays[0] > todayNumber || todayNumber - uniqueDays[0] > 1) return 0;
  let total = 1;
  for (let i = 1; i < uniqueDays.length && uniqueDays[i - 1] - uniqueDays[i] === 1; i++) total++;
  return total;
}
export const levels = [
  { count: 0, title: 'Khoji Noob', badge: 'noob' }, { count: 5, title: 'Khoji Rookie', badge: 'rookie' },
  { count: 10, title: 'Khoji Scout', badge: 'scout' }, { count: 50, title: 'Khoji Explorer', badge: 'explorer' },
  { count: 100, title: 'Khoji Pathfinder', badge: 'pathfinder' }, { count: 500, title: 'Khoji Sage', badge: 'sage' },
  { count: 1000, title: 'Khoji Legend', badge: 'legend' },
] as const;
export function levelFor(count: number) { return levels.filter(l => l.count <= count).at(-1) ?? levels[0]; }
export function nextLevel(count: number) { return levels.find(l => l.count > count); }
export function recordVisit(journal: Journal, storyId: string, index: number, timestamp = Date.now()): Journal {
  const story = stories.find(s => s.id === storyId);
  if (!story || !Number.isInteger(index) || index < 0 || index >= story.slides.length) return journal;
  const edition=story.contentRevision??2,previous=journal.progress[storyId];
  const old=previous?.contentRevision!==undefined&&previous.contentRevision!==edition?undefined:previous;
  return { ...journal, progress: { ...journal.progress, [storyId]: { index, visited: [...new Set([...(old?.visited ?? []), index])], updatedAt: timestamp, contentRevision:edition } } };
}
export function completeStory(journal: Journal, id: string, now = new Date()): Journal {
  const story = stories.find(s => s.id === id);
  if (!story || journal.completed[id] || !story.slides.every((_, i) => journal.progress[id]?.visited.includes(i))) return journal;
  const date = localDate(now);
  return { ...journal, completed: { ...journal.completed, [id]: { date, timestamp: now.getTime() } }, activeDays: [...new Set([...journal.activeDays, date])].sort() };
}
export function toggleSaved(journal: Journal, id: string): Journal {
  if (!knownIds.has(id)) return journal;
  const removing=journal.saved.includes(id),bookmarks={...journal.bookmarks};
  if(removing)delete bookmarks[id];
  return { ...journal, bookmarks, saved: removing ? journal.saved.filter(s => s !== id) : [...journal.saved, id] };
}
export function saveBookmark(journal:Journal,id:string,details:BookmarkDetails):Journal {
  if(!knownIds.has(id)||!validBookmark(details))return journal;
  return {...journal,saved:[...new Set([...journal.saved,id])],bookmarks:{...journal.bookmarks,[id]:{...details}}};
}
export function bookmarkFolders(journal:Journal):string[]{
  return [...new Set(journal.saved.map(id=>journal.bookmarks?.[id]?.folder).filter((name):name is string=>Boolean(name)))].sort((a,b)=>a.localeCompare(b));
}
export function toggleReaction(journal: Journal, id: string, reaction: 'like' | 'dislike'): Journal {
  if (!knownIds.has(id)) return journal;
  const reactions = { ...journal.reactions };
  if (reactions[id] === reaction) delete reactions[id]; else reactions[id] = reaction;
  return { ...journal, reactions };
}

export function relatedStories(story: Story, journal: Journal, limit = stories.length) {
  const affinity = (item: Story) => (storyInCategory(item, story.category) ? 8 : 0) + item.tags.filter(tag => story.tags.includes(tag)).length * 3;
  return stories.filter(item => item.id !== story.id && journal.reactions[item.id] !== 'dislike'
      && (storyInCategory(item, story.category) || item.tags.slice(2).some(tag => story.tags.slice(2).includes(tag))))
    .sort((a,b) => Number(Boolean(journal.completed[a.id])) - Number(Boolean(journal.completed[b.id])) || affinity(b) - affinity(a))
    .slice(0, limit);
}
